import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';

export const useMissions = () => {
  const { apiCall, API_BASE_URL } = useApi();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMissions = useCallback(async () => {
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les missions');
      setMissions([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      const data = await apiCall(`/missions`);
      console.log('✅ Missions API Success:', data);
      setMissions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Erreur lors du chargement des missions:', err);
      setError(err.message);
      setMissions([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall, API_BASE_URL]);

  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  const refreshMissions = useCallback(() => {
    return fetchMissions();
  }, [fetchMissions]);

  return {
    missions,
    loading,
    error,
    refreshMissions
  };
};

export const useMission = (missionId) => {
  const { apiCall, API_BASE_URL } = useApi();
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Plus de données de démo - utiliser uniquement les données de l'API

  const fetchMission = useCallback(async () => {
    if (!missionId) return;
    
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger la mission');
      setMission(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await apiCall(`/missions/${missionId}`);
      console.log('✅ Mission API Success:', data);
      setMission(data);
    } catch (err) {
      console.error('❌ Erreur lors du chargement de la mission:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [missionId, apiCall, API_BASE_URL]);

  useEffect(() => {
    fetchMission();
  }, [fetchMission]);

  const updateMissionStatus = useCallback(async (status) => {
    try {
      const updatedMission = await apiCall(`/missions/${missionId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });

      setMission(prev => ({ ...prev, ...updatedMission }));
      return updatedMission;
    } catch (err) {
      console.error('Erreur lors de la mise à jour du statut:', err);
      throw err;
    }
  }, [missionId, apiCall]);

  const sendWarning = useCallback(async (message) => {
    try {
      const result = await apiCall(`/missions/${missionId}/warning`, {
        method: 'POST',
        body: JSON.stringify({ message })
      });

      return result;
    } catch (err) {
      console.error('Erreur lors de l\'envoi de l\'avertissement:', err);
      throw err;
    }
  }, [missionId, apiCall]);

  const refreshMission = useCallback(() => {
    return fetchMission();
  }, [fetchMission]);

  return {
    mission,
    loading,
    error,
    updateMissionStatus,
    sendWarning,
    refreshMission
  };
};

export const useMissionNotes = (missionId) => {
  const { apiCall } = useApi();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNotes = useCallback(async () => {
    if (!missionId) return;
    
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les notes');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await apiCall(`/missions/${missionId}/notes`);
      setNotes(data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des notes:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [missionId, apiCall]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const addNote = useCallback(async (content) => {
    try {
      const newNote = await apiCall(`/missions/${missionId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ content })
      });

      setNotes(prev => [newNote, ...prev]);
      return newNote;
    } catch (err) {
      console.error('Erreur lors de l\'ajout de la note:', err);
      throw err;
    }
  }, [missionId, apiCall]);

  const refreshNotes = useCallback(() => {
    return fetchNotes();
  }, [fetchNotes]);

  return {
    notes,
    loading,
    error,
    addNote,
    refreshNotes
  };
};

// Hook pour récupérer le pricing d'une mission
export const useMissionPricing = (missionId) => {
  const { apiCall } = useApi();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPricing = useCallback(async () => {
    if (!missionId) {
      setLoading(false);
      return;
    }
    
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger le pricing');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await apiCall(`/missions/${missionId}`);
      console.log('✅ Mission pricing API Success:', data);
      
      // Le pricing est maintenant inclus dans la réponse de /api/missions/:id
      if (data.pricing) {
        setPricing(data.pricing);
      } else {
        // Fallback si pricing n'est pas disponible
        setPricing({
          estimated_price: data.amount || 0,
          accepted_price: data.amount || 0,
          final_price: data.amount || 0,
          commission_rate: 0,
          commission_amount: 0,
          prestataire_earnings: data.amount || 0
        });
      }
    } catch (err) {
      console.error('❌ Erreur lors du chargement du pricing:', err);
      setError(err.message);
      setPricing(null);
    } finally {
      setLoading(false);
    }
  }, [missionId, apiCall]);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  const refreshPricing = useCallback(() => {
    return fetchPricing();
  }, [fetchPricing]);

  return {
    pricing,
    loading,
    error,
    refetch: refreshPricing
  };
};